/** Éditeur de texte riche (TipTap) utilisé par les notes commerciales.
 *
 *  Une note d'appel est rarement un paragraphe plat : listes de points à
 *  reprendre, éléments en gras qu'on doit retrouver d'un coup d'œil, citation
 *  de l'objection du client. Le HTML produit est réassaini côté serveur
 *  (crm/html_notes.py) : le rendu peut donc être inséré tel quel.
 */
import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Quote, Code,
  Heading2, Heading3, AlignLeft, AlignCenter, AlignRight, Link2, Undo2, Redo2,
  RemoveFormatting,
} from "lucide-react";

/** Palette volontairement courte : marquer une urgence ou une alerte, pas
 *  décorer. Les valeurs sont celles de la charte (slate/rose/amber/emerald). */
const COLORS = [
  { value: null, label: "Par défaut", swatch: "#334155" },
  { value: "#e11d48", label: "Rouge (alerte)", swatch: "#e11d48" },
  { value: "#d97706", label: "Ambre (à surveiller)", swatch: "#d97706" },
  { value: "#059669", label: "Vert (positif)", swatch: "#059669" },
  { value: "#4f46e5", label: "Indigo", swatch: "#4f46e5" },
];

export const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } },
    codeBlock: false,
    horizontalRule: false,
  }),
  TextStyle,
  Color,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
];

function Btn({ onClick, active, disabled, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-30 ${active ? "bg-indigo-100 text-indigo-700" : "text-slate-500 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;
  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Adresse du lien (vide pour retirer)", previous);
    if (url === null) return;
    if (!url.trim()) return editor.chain().focus().unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-1.5 py-1 bg-white rounded-t-lg">
      <Btn title="Gras (Ctrl+B)" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></Btn>
      <Btn title="Italique (Ctrl+I)" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></Btn>
      <Btn title="Souligné (Ctrl+U)" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><Underline size={14} /></Btn>
      <Btn title="Barré" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={14} /></Btn>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <Btn title="Titre" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14} /></Btn>
      <Btn title="Sous-titre" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={14} /></Btn>
      <Btn title="Liste à puces" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></Btn>
      <Btn title="Liste numérotée" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></Btn>
      <Btn title="Citation" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={14} /></Btn>
      <Btn title="Code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={14} /></Btn>
      <Btn title="Lien" active={editor.isActive("link")} onClick={setLink}><Link2 size={14} /></Btn>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <Btn title="Aligner à gauche" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14} /></Btn>
      <Btn title="Centrer" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14} /></Btn>
      <Btn title="Aligner à droite" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14} /></Btn>
      <span className="w-px h-4 bg-slate-200 mx-1" />
      <div className="flex items-center gap-1 px-1">
        {COLORS.map((c) => (
          <button key={c.label} type="button" title={c.label}
            onClick={() => (c.value ? editor.chain().focus().setColor(c.value).run() : editor.chain().focus().unsetColor().run())}
            className="h-4 w-4 rounded-full border border-white ring-1 ring-slate-200 hover:scale-110 transition-transform"
            style={{ background: c.swatch }} />
        ))}
      </div>
      <Btn title="Effacer la mise en forme" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}><RemoveFormatting size={14} /></Btn>
      <span className="ml-auto flex items-center">
        <Btn title="Annuler (Ctrl+Z)" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} /></Btn>
        <Btn title="Rétablir" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={14} /></Btn>
      </span>
    </div>
  );
}

/**
 * @param value      HTML courant
 * @param onChange   (html, texte) — le texte sert au compteur et aux garde-fous
 * @param onSubmit   Ctrl/Cmd+Entrée
 */
export default function RichTextEditor({
  value, onChange, onSubmit, placeholder = "Écrire…", minHeight = 110, autoFocus = false,
}) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || "",
    autofocus: autoFocus,
    editorProps: {
      // Correcteur du navigateur actif, en français : les fautes se soulignent
      // pendant la frappe ; la correction IA du bouton vient en complément.
      attributes: { class: "note-content px-3 py-2.5 outline-none", style: `min-height:${minHeight}px`,
                    spellcheck: "true", lang: "fr" },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          onSubmit?.();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => onChange?.(ed.isEmpty ? "" : ed.getHTML(), ed.getText()),
  });

  // Réinitialisation externe (formulaire vidé après enregistrement) sans
  // écraser la frappe en cours.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "", { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    <div className="rounded-lg border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-indigo-500">
      <Toolbar editor={editor} />
      <div className="relative">
        {editor?.isEmpty && (
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-slate-400">{placeholder}</span>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/** Rendu en lecture seule d'une note. Le HTML vient du serveur, qui l'a
 *  filtré sur liste blanche — voir crm/html_notes.py. */
export function RichTextView({ html, className = "" }) {
  return <div className={`note-content ${className}`} dangerouslySetInnerHTML={{ __html: html || "" }} />;
}
